// Layer 4 (UI) — the 5 × 5 heat map (polish.P20, comp A `.hm`): consequence up,
// likelihood across, each cell a soft band wash; risk ids carry state ink only
// in Critical and High cells, and an empty cell shows its L × C score.
// Ids wrap two to a line where the cell is wide enough (as the comp sets
// them) and one to a line where it is not; the row grows rather than clips.
// Each id keeps the comp's inset from the cell border (≥ 4 px measured), so the
// negative tracking is gone and the gap is an en-space. Selecting an occupied
// cell rings it. Renders `riskHeatMap(mode)` only.

import { Fragment } from 'react';
import { cn } from '@/shared/cn';
import { riskHeatMap } from './RiskHeatMap.transform';
import type { RiskConsequenceMode } from './Scene.transform';

export function RiskHeatMap({
  mode,
  selectedCell,
  onSelectCell,
}: {
  mode: RiskConsequenceMode;
  selectedCell: string | null;
  onSelectCell: (key: string | null) => void;
}) {
  const view = riskHeatMap(mode);
  return (
    <div data-risk-heat-map={view.mode}>
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.07em] text-muted">Consequence ↑</p>
      <div
        role="grid"
        aria-label="Risk heat map: consequence up, likelihood across"
        className="grid grid-cols-[18px_repeat(5,minmax(0,1fr))] gap-[3px] [grid-auto-rows:minmax(46px,auto)]"
      >
        {view.rows.map((row) => (
          <Fragment key={row.consequence}>
            <span className="grid place-items-center text-[10px] tabular-nums text-muted">{row.consequence}</span>
            {row.cells.map((cell) => {
              const occupied = cell.riskIds.length > 0;
              const selected = selectedCell === cell.key;
              return (
                <button
                  key={cell.key}
                  type="button"
                  role="gridcell"
                  disabled={!occupied}
                  aria-selected={selected}
                  aria-label={occupied ? `${cell.riskIds.join(', ')} · likelihood ${cell.likelihood} × consequence ${cell.consequence}` : `score ${cell.score}`}
                  data-heat-cell={cell.key}
                  data-heat-band={cell.band}
                  onClick={() => onSelectCell(selected ? null : cell.key)}
                  className={cn(
                    'flex min-w-0 flex-col items-center justify-center gap-px overflow-hidden rounded-md border px-[5px] py-0.5 text-[10px] tabular-nums transition-colors duration-150',
                    occupied ? 'cursor-pointer hover:!bg-[var(--surface-2)]' : 'cursor-default',
                    selected && 'shadow-[inset_0_0_0_1.5px_var(--color-accent-500)]',
                  )}
                  style={{ background: cell.fill, borderColor: cell.border }}
                >
                  {occupied ? (
                    <span className="flex max-w-full flex-wrap justify-center gap-x-[5px]">
                      {cell.riskIds.map((id) => (
                        <span
                          key={id}
                          className="whitespace-nowrap font-semibold leading-[15px]"
                          style={{ color: cell.ink }}
                        >
                          {id}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span className="text-muted opacity-75">{cell.score}</span>
                  )}
                </button>
              );
            })}
          </Fragment>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-[18px_repeat(5,minmax(0,1fr))] gap-[3px] text-center text-[10px] tabular-nums text-muted" aria-hidden>
        <span />
        {view.likelihoodTicks.map((tick) => (
          <span key={tick}>{tick}</span>
        ))}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2.5 text-[11px] text-muted">
        <span>Likelihood →</span>
        <span className="flex flex-wrap gap-3">
          {view.legend.map((stop) => (
            <span key={stop.band} className="inline-flex items-center gap-1.5">
              <i aria-hidden className="inline-block size-2.5 rounded-[3px] border border-[var(--hairline)]" style={{ background: stop.fill }} />
              {stop.label}
            </span>
          ))}
        </span>
      </div>
    </div>
  );
}
